/**
 * CSVPreviewModal Component
 * Modal dialog for previewing and validating CSV data before bulk import with TAP-only authentication.
 * TAP (Temporary Access Pass) is the only authentication method for bulk-created users.
 * Shows first 10 rows, validates required columns (excluding password), and confirms import.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { Alert } from '../../common/Alert';
import type { ParsedCSVData } from '../csv';
import type { UserCreateRequest } from '../../../types/user';

interface CSVPreviewModalProps {
  isOpen: boolean;
  csvData: ParsedCSVData | null;
  onClose: () => void;
  onConfirm: (users: UserCreateRequest[]) => void;
}

const useStyles = makeStyles({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase300,
  },
  th: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  infoText: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
  summaryText: {
    marginBottom: tokens.spacingVerticalM,
  },
});

const REQUIRED_COLUMNS = ['displayName', 'userPrincipalName', 'mobilePhone', 'accountEnabled'];
const OPTIONAL_COLUMNS = [
  'givenName',
  'surname',
  'jobTitle',
  'department',
  'usageLocation',
  'streetAddress',
  'state',
  'country',
  'physicalDeliveryOfficeName',
  'city',
  'postalCode',
  'telephoneNumber',
];

/**
 * Normalize phone number to E.164 format.
 * - If already starts with "+", keep as-is
 * - If 10 digits, prepend "+1" (US number without country code)
 * - If 11 digits starting with "1", prepend "+" (US number with country code)
 * - Otherwise, prepend "+" (international number with country code included by user)
 */
function normalizePhoneNumber(phone: string): string {
  // Remove whitespace and common separators
  const cleaned = phone.replace(/[\s\-().']/g, '');

  // Already E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // 10 digits - US number without country code
  if (/^\d{10}$/.test(cleaned)) {
    return `+1${cleaned}`;
  }

  // 11 digits starting with 1 - US number with country code
  if (/^1\d{10}$/.test(cleaned)) {
    return `+${cleaned}`;
  }

  // Assume international with country code, just add +
  return `+${cleaned}`;
}

export function CSVPreviewModal({ isOpen, csvData, onClose, onConfirm }: CSVPreviewModalProps) {
  const styles = useStyles();
  const [isProcessing, setIsProcessing] = useState(false);

  if (!csvData) {
    return null;
  }

  // Validate required columns
  const missingColumns = REQUIRED_COLUMNS.filter(
    (col) => !csvData.headers.includes(col)
  );

  const hasValidationErrors = missingColumns.length > 0;

  const handleConfirm = () => {
    if (hasValidationErrors) {
      return;
    }

    setIsProcessing(true);

    try {
      // Convert CSV rows to UserCreateRequest objects
      const users: UserCreateRequest[] = csvData.rows.map((row) => {
        // Trim whitespace from all values to handle spaces after commas in CSV
        const trimmedRow: Record<string, string> = {};
        Object.keys(row).forEach(key => {
          trimmedRow[key] = row[key]?.trim() || '';
        });

        // Extract mail nickname from UPN (part before @)
        const mailNickname = trimmedRow.userPrincipalName.split('@')[0];

        const user: UserCreateRequest = {
          display_name: trimmedRow.displayName,
          user_principal_name: trimmedRow.userPrincipalName,
          mail_nickname: mailNickname,
          // TAP authentication (only method available)
          use_tap: true,
          tap_lifetime_minutes: csvData.tapConfig?.tap_lifetime_minutes || 60,
          tap_one_time_use: csvData.tapConfig?.tap_one_time_use !== undefined ? csvData.tapConfig.tap_one_time_use : true,
          mobile_phone: normalizePhoneNumber(trimmedRow.mobilePhone), // REQUIRED for TAP delivery (normalized to E.164)
          // Force SMS delivery for TAP (mobile phone is required, so always send via SMS)
          send_via_sms: true,
          // AAD uses "No" for enabled, "Yes" for blocked - we need account_enabled boolean
          account_enabled: trimmedRow.accountEnabled?.toLowerCase() !== 'yes',
          usage_location: trimmedRow.usageLocation || 'US',
        };

        // Add optional fields if present (after trimming)
        if (trimmedRow.givenName) user.given_name = trimmedRow.givenName;
        if (trimmedRow.surname) user.surname = trimmedRow.surname;
        if (trimmedRow.jobTitle) user.job_title = trimmedRow.jobTitle;
        if (trimmedRow.department) user.department = trimmedRow.department;
        if (trimmedRow.physicalDeliveryOfficeName) user.office_location = trimmedRow.physicalDeliveryOfficeName;
        if (trimmedRow.telephoneNumber) user.office_phone = trimmedRow.telephoneNumber;
        if (trimmedRow.streetAddress) user.street_address = trimmedRow.streetAddress;
        if (trimmedRow.city) user.city = trimmedRow.city;
        if (trimmedRow.state) user.state = trimmedRow.state;
        if (trimmedRow.postalCode) user.postal_code = trimmedRow.postalCode;
        if (trimmedRow.country) user.country = trimmedRow.country;

        return user;
      });

      onConfirm(users);
    } catch (error) {
      // This shouldn't happen during CSV parsing, but log it for debugging
      // eslint-disable-next-line no-console
      console.error('Error preparing user data:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Preview first 10 rows
  const previewRows = csvData.rows.slice(0, 10);
  const hasMoreRows = csvData.rows.length > 10;

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface data-testid="csv-preview-modal" style={{ maxWidth: '900px' }}>
        <DialogBody>
          <DialogTitle>Preview CSV Import</DialogTitle>
          <DialogContent>
            <div className={styles.summaryText}>
              <Text weight="semibold">File: {csvData.fileName}</Text>
              <br />
              <Text>Total users to import: {csvData.rows.length}</Text>
              {hasMoreRows && (
                <Text className={styles.infoText}>
                  (Showing first {previewRows.length} rows)
                </Text>
              )}
            </div>

            {hasValidationErrors && (
              <Alert intent="warning" title="Missing required columns">
                {missingColumns.join(', ')}
                <br />
                <Text className={styles.infoText}>
                  Required columns: {REQUIRED_COLUMNS.join(', ')}
                </Text>
              </Alert>
            )}

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {csvData.headers.map((header) => (
                      <th key={header} className={styles.th}>
                        {header}
                        {REQUIRED_COLUMNS.includes(header) && (
                          <Text style={{ color: tokens.colorPaletteRedForeground1 }}> *</Text>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => (
                    <tr key={index}>
                      {csvData.headers.map((header) => (
                        <td key={header} className={styles.td}>
                          {row[header] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Text className={styles.infoText}>
              Optional columns: {OPTIONAL_COLUMNS.join(', ')}
            </Text>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isProcessing}
              data-testid="csv-preview-cancel-button"
            >
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleConfirm}
              disabled={hasValidationErrors || isProcessing}
              loading={isProcessing}
              loadingText="Importing..."
              data-testid="csv-preview-confirm-button"
            >
              {`Import ${csvData.rows.length} User${csvData.rows.length !== 1 ? 's' : ''}`}
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
